// src/contacts/import.js — двустъпков внос на контакти от Excel (Фаза Б.7).
//
// ЗАЩО: досега записите влизаха НЕОБРАТИМО още с избора на файл. Същият
// файл втори път удвояваше всички клиенти, а „пропуснати: 3" не казваше
// кои. Сега пътят е като при вноса в „Такси": преглед → изричен „Внеси".
//
// classifyImportRows е ЧИСТА функция (лесна за проверка): разделя редовете
// на готови / без име / съвпадащи по ЕИК. Дублите се търсят и срещу базата,
// и ВЪТРЕ в самия файл (един ЕИК два пъти в него).
//
// Промени 16.10.2026, т. 3ж: същият внос попълва и ШИФРИТЕ. Файл с
// номенклатурата на клиентите от Конто (код, име, Булстат) → заварен контакт
// със същия ЕИК и без шифър получава шифъра от файла. Така файлът за Конто
// носи кодовете, които Конто познава, и Конто не създава нови (дълги).
// Различен заварен шифър не се пипа без изрична отметка.

import { el, clear } from '../shared/dom.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal } from '../shared/modal.js';
import { state, contacts as contactsCache, setContacts, cq, isAdminUser } from '../shared/state.js';
import { isFakeEik } from '../../services/kontoPartners.js';
import { kontoSyncDialog } from '../companies/kontoSync.js';

const TYPES = ['client', 'lead', 'partner', 'supplier'];
const pick = (r, keys) => { for (const k of keys) { if (r[k] != null && String(r[k]).trim() !== '') return String(r[k]).trim(); } return ''; };
const normEik = (v) => String(v || '').replace(/\s+/g, '');
// Истински ЕИК за сравнение: 999999999 / 9999999999 („Население", физически
// лица) е измислен и много различни клиенти го носят — не е дубликат и по
// него не се търси (ОТГОВОР-ПРОМТ-21).
const realEik = (v) => { const e = normEik(v); return e && !isFakeEik(e) ? e : ''; };

// Ред от файла → запис за сървъра. Имената на колоните са същите като в
// макета (и приемат латинските ключове, ако файлът е изнесен отвън).
// Имената на колоните от Конто (номенклатурата на контрагентите) също се
// приемат: „Код", „Наименование", „Булстат", „ИН по ДДС"…
export function mapImportRow(r, companyId) {
  const vid = pick(r, ['Вид', 'type']).toLowerCase();
  return {
    company_id: companyId,
    name: pick(r, ['Име', 'Name', 'name', 'Фирма', 'Наименование', 'Име на контрагента', 'Име на партньора']),
    type: TYPES.indexOf(vid) >= 0 ? vid : 'client',
    eik: pick(r, ['ЕИК', 'ЕИК / Булстат', 'eik', 'Булстат', 'БУЛСТАТ', 'ЕИК/Булстат', 'Булстат на контрагента']),
    vat_number: pick(r, ['ДДС номер', 'vat_number', 'ДДС', 'ИН по ДДС', 'ИН по ДДС на контрагента', 'ДДС №']),
    mol: pick(r, ['МОЛ', 'mol', 'Име на МОЛ']),
    city: pick(r, ['Град', 'city']),
    address: pick(r, ['Адрес', 'address', 'Адрес на контрагента']),
    phone: pick(r, ['Телефон', 'phone']),
    email: pick(r, ['Имейл', 'email']),
    website: pick(r, ['Уебсайт', 'website']),
    acc_code: pick(r, ['Счетоводен шифър', 'Сч. шифър', 'acc_code', 'Код', 'Шифър', 'Код на контрагента', 'Код на партньора']),
    supplier_code: pick(r, ['Шифър като доставчик', 'supplier_code']),
  };
}

// opts.kontoFile: файлът е номенклатурата от Конто. Тогава ред без истински
// ЕИК и без ДДС номер („?", „Приключване", „Население", физически лица) НЕ
// става контакт — кодът му пак влиза в списъка на заетите (kontoCodeList).
export function classifyImportRows(rows, existing, companyId, opts) {
  const kontoFile = !!(opts && opts.kontoFile);
  const have = new Map();
  const byEik = new Map();
  (existing || []).forEach((c) => { const k = realEik(c.eik); if (k) { have.set(k, c.name || ''); if (!byEik.has(k)) byEik.set(k, c); } });
  const ready = [], noName = [], dupes = [], codeFill = [], codeConflict = [], serviceRows = [];
  const coded = new Set();
  (rows || []).forEach((r, idx) => {
    const payload = mapImportRow(r, companyId || (state().activeCompany || null));
    const row = idx + 1;
    const eik = realEik(payload.eik);
    // Шифър от файла за заварен контакт със същия ЕИК — и при ред без име
    // (номенклатурата може да носи само код и Булстат).
    const ex = eik ? byEik.get(eik) : null;
    const code = String(payload.acc_code || '').trim();
    if (ex && code && !coded.has(ex.id)) {
      coded.add(ex.id);
      const curCode = String(ex.acc_code || '').trim();
      if (!curCode) codeFill.push({ row, contact: ex, code });
      else if (curCode !== code) codeConflict.push({ row, contact: ex, code, current: curCode });
    }
    if (!payload.name) { if (!(ex && code)) noName.push({ row, payload }); return; }
    if (kontoFile && !eik && !String(payload.vat_number || '').trim()) { serviceRows.push({ row, payload }); return; }
    if (eik && have.has(eik)) {
      const other = have.get(eik);
      dupes.push({ row, payload, reason: 'ЕИК ' + eik + (other ? ' — вече е зает от „' + other + '"' : ' се повтаря във файла') });
      return;
    }
    // Запомняме и новия ЕИК, за да хванем и втори ред със същия ВЪВ файла.
    if (eik) have.set(eik, payload.name);
    ready.push({ row, payload });
  });
  return { ready, noName, dupes, codeFill, codeConflict, serviceRows, total: (rows || []).length };
}

// Етап 3: файлът с клиентите от Конто е и СПИСЪКЪТ на кодовете, които Конто
// вече познава (всички редове с код — и тези, които не са контакти в CRM-а).
// От него CRM-ът дава на новите клиенти свободен код и не хваща зает
// (services/kontoPartners.js). Познава се по колоните на Конто.
export function kontoCodeList(rows) {
  const list = [];
  let konto = false;
  (rows || []).forEach((r) => {
    if (r && ('Код' in r || 'Булстат' in r || 'Код на контрагента' in r || 'Код на партньора' in r)) konto = true;
    const p = mapImportRow(r || {}, null);
    const c = String(p.acc_code || '').trim();
    if (c) list.push({ c, e: normEik(p.eik) });
  });
  return { list, konto };
}

// Прегледът. Записва се само при изричен „Внеси N".
// Таблицата с контрагентите на ВСИЧКИ фирми от Конто (ПРОМТ-23) — познава се
// по колоната „ЕИК на фирмата". Тук не се внася: всички редове биха влезли в
// избраната фирма, кръстени на фирмата от Конто (колоната „Фирма") — точно
// това стана на 27.10.2026 (597 контакта „АКУЛА…", „АЛГЕМА…" във ВИКОНТИ).
export function isAllFirmsTable(rows) {
  return (rows || []).slice(0, 5).some((r) => r && 'ЕИК на фирмата' in r);
}

function allFirmsNotice(file) {
  const admin = isAdminUser();
  const body = el('div', {},
    el('p', { class: 'sev-warn', role: 'alert', text: 'Това е таблицата с контрагентите на всички фирми от Конто. Тук не се внася — всички редове биха влезли в една фирма.' }),
    el('p', { text: admin
      ? 'Внася се от Мои фирми → „Внос от Конто“: там всеки ред отива в своята фирма. Натиснете бутона долу — файлът е вече избран.'
      : 'Внася се от администратора: Мои фирми → „Внос от Конто“.' }));
  const foot = el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Затвори', onClick: closeModal }));
  if (admin) foot.appendChild(el('button', { class: 'btn', type: 'button', text: 'Отвори „Внос от Конто“', style: 'margin-left:8px', onClick: () => { closeModal(); kontoSyncDialog(file); } }));
  openModal({ title: 'Грешното място за този файл', body, foot });
}

export function contactsImportPreview(rows, file) {
  if (isAllFirmsTable(rows)) { allFirmsNotice(file); return; }
  const companyId = state().activeCompany;
  const res = classifyImportRows(rows, contactsCache(), companyId, { kontoFile: kontoCodeList(rows).konto });

  const listOf = (title, arr, lineOf) => arr.length
    ? el('div', { style: 'margin-top:12px' },
        el('div', { style: 'font-weight:600;margin-bottom:4px', text: title }),
        el('div', { class: 'imp-list' }, ...arr.slice(0, 200).map((x) => el('div', { class: 'imp-line', text: lineOf(x) }))),
        arr.length > 200 ? el('div', { style: 'color:var(--muted);font-size:12px', text: '…и още ' + (arr.length - 200) }) : null)
    : null;

  const summary = el('div', {},
    el('p', { style: 'margin:0 0 6px', text: 'Прочетени редове: ' + res.total + '.' }),
    el('ul', { style: 'margin:0;padding-left:18px' },
      el('li', { text: 'Ще се внесат ' + res.ready.length + ' нови записа.' }),
      res.noName.length ? el('li', { text: res.noName.length + (res.noName.length === 1 ? ' ред без име — ще се пропусне.' : ' реда без име — ще се пропуснат.') }) : null,
      res.dupes.length ? el('li', { text: res.dupes.length + (res.dupes.length === 1 ? ' запис съвпада по ЕИК — ще се пропусне.' : ' записа съвпадат по ЕИК — ще се пропуснат.') }) : null,
      res.serviceRows.length ? el('li', { text: res.serviceRows.length + ' от Конто без истински ЕИК и ДДС номер (население, физически лица, служебни) — не стават контакти, но кодовете им се пазят като заети.' }) : null,
      res.codeFill.length ? el('li', { text: res.codeFill.length + (res.codeFill.length === 1 ? ' заварен контакт е без счетоводен шифър — файлът има шифър за него.' : ' заварени контакта са без счетоводен шифър — файлът има шифри за тях.') }) : null));

  // Шифрите (т. 3ж): празните се попълват по подразбиране; различните — само
  // ако човекът изрично отметне (може шифърът в CRM-а да е верният).
  const fillCb = el('input', { type: 'checkbox', checked: true });
  const confCb = el('input', { type: 'checkbox' });
  // Списъкът от Конто (Етап 3) — само кантората; отметнат, когато файлът е с
  // колоните на Конто.
  const staff = !!(state().user && state().user.role !== 'client');
  const kl = kontoCodeList(rows);
  const listCb = el('input', { type: 'checkbox', checked: kl.konto });
  const withList = () => staff && kl.list.length && listCb.checked;
  const goBtn = el('button', { class: 'btn' });
  const jobs = () => (fillCb.checked ? res.codeFill : []).concat(confCb.checked ? res.codeConflict : []);
  const relabel = () => {
    const n = jobs().length;
    const parts = [];
    if (res.ready.length) parts.push('Внеси ' + res.ready.length);
    if (n) parts.push((parts.length ? 'и попълни ' : 'Попълни ') + n + (n === 1 ? ' шифър' : ' шифъра'));
    if (withList()) parts.push((parts.length ? 'и запомни ' : 'Запомни ') + 'списъка от Конто');
    goBtn.textContent = parts.join(' ');
    goBtn.disabled = !parts.length;
  };
  fillCb.addEventListener('change', relabel);
  confCb.addEventListener('change', relabel);
  listCb.addEventListener('change', relabel);
  goBtn.addEventListener('click', () => doImport(res.ready, jobs(), withList() ? kl.list : null));
  const codeBlock = (res.codeFill.length || res.codeConflict.length) ? el('div', { style: 'margin-top:12px' },
    res.codeFill.length ? el('label', { class: 'choice' }, fillCb, el('span', { text: 'Попълни шифрите на заварените контакти по ЕИК (' + res.codeFill.length + ')' })) : null,
    listOf('Ще получат шифър:', res.codeFill, (x) => 'ред ' + x.row + ': ' + (x.contact.name || '') + ' → ' + x.code),
    res.codeConflict.length ? el('label', { class: 'choice', style: 'margin-top:8px' }, confCb, el('span', { text: 'Замени и различните шифри (' + res.codeConflict.length + ') — само ако файлът е от Конто и е по-верен' })) : null,
    listOf('С различен шифър (не се пипат без отметката):', res.codeConflict, (x) => 'ред ' + x.row + ': ' + (x.contact.name || '') + ' — в CRM „' + x.current + '", във файла „' + x.code + '"')) : null;

  const listBlock = staff && kl.list.length ? el('div', { style: 'margin-top:12px' },
    el('label', { class: 'choice' }, listCb, el('span', { text: 'Това е списъкът на клиентите от Конто — запомни кодовете му (' + kl.list.length + ') в настройките на фирмата.' }))) : null;
  const body = el('div', {},
    summary,
    listOf('Ще се внесат:', res.ready, (x) => 'ред ' + x.row + ': ' + x.payload.name + (x.payload.eik ? ' (ЕИК ' + x.payload.eik + ')' : '')),
    listOf('Пропуснати — без име:', res.noName, (x) => 'ред ' + x.row + ' (без име)'),
    listOf('Пропуснати — съвпадат по ЕИК:', res.dupes, (x) => 'ред ' + x.row + ': ' + x.payload.name + ' — ' + x.reason),
    listOf('Не стават контакти (без истински ЕИК и ДДС номер):', res.serviceRows, (x) => 'ред ' + x.row + ': ' + x.payload.name + (x.payload.acc_code ? ' (код ' + x.payload.acc_code + ')' : '')),
    codeBlock,
    listBlock,
    !res.ready.length && !res.codeFill.length && !res.codeConflict.length && !listBlock ? el('p', { style: 'margin-top:12px;font-weight:600', text: 'Няма нови записи за внасяне.' }) : null);

  const foot = el('div', {});
  if (res.ready.length || res.codeFill.length || res.codeConflict.length || listBlock) {
    foot.appendChild(el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }));
    relabel();
    foot.appendChild(goBtn);
  } else {
    foot.appendChild(el('button', { class: 'btn', text: 'Затвори', onClick: closeModal }));
  }
  openModal({ title: 'Преглед преди внос на контакти', body, foot, wide: true });
}

async function doImport(ready, codeJobs, kontoList) {
  closeModal();
  toast('Внасям…');
  let ok = 0, codes = 0, listSaved = 0; const failed = [];
  for (const x of ready) {
    try { await api('/contacts', { method: 'POST', body: JSON.stringify(x.payload) }); ok++; }
    catch (e) { failed.push('ред ' + x.row + ': ' + x.payload.name + ' — ' + e.message); }
  }
  // Редакцията на контакт заменя всичките му полета — затова тръгва целият
  // заварен запис, само с новия шифър.
  for (const j of (codeJobs || [])) {
    try { await api('/contacts/' + j.contact.id, { method: 'PUT', body: JSON.stringify(Object.assign({}, j.contact, { acc_code: j.code })) }); codes++; }
    catch (e) { failed.push('ред ' + j.row + ': шифър за „' + (j.contact.name || '') + '" — ' + e.message); }
  }
  if (kontoList && kontoList.length) {
    try {
      const co = await api('/companies/' + state().activeCompany + '/konto-partners', { method: 'PUT', body: JSON.stringify({ customer: kontoList }) });
      listSaved = (co.konto_partners && co.konto_partners.count) || kontoList.length;
      const list = state().companies || [];
      const i = list.findIndex((c) => String(c.id) === String(co.id));
      if (i >= 0) list[i] = Object.assign({}, list[i], { konto_partners: co.konto_partners });
    } catch (e) { failed.push('списъкът от Конто — ' + e.message); }
  }
  toast('Внесени: ' + ok + (codes ? ('. Попълнени шифри: ' + codes) : '') + (listSaved ? ('. Запомнен списък от Конто: ' + listSaved + ' кода') : '') + (failed.length ? (', с грешка: ' + failed.length) : '') + '.');
  // Провалените се ИЗБРОЯВАТ — „с грешка: 3" само по себе си не помага.
  if (failed.length) {
    openModal({
      title: 'Тези записи не влязоха',
      body: el('div', { class: 'imp-list' }, ...failed.map((t) => el('div', { class: 'imp-line', text: t }))),
      foot: el('div', {}, el('button', { class: 'btn', text: 'Затвори', onClick: closeModal })),
    });
  }
  try { setContacts(await api('/contacts' + cq())); } catch (e) {}
  if (state().view === 'contacts') window.CRM.renderView(document.getElementById('page'));
}
